/**
 * Harn-tasks store — the polled snapshot of standalone harness tasks
 * (i.e. those NOT dispatched by mission-service as DAG leaves).
 *
 * Direct mirror of `useMissionsStore`: Map keyed by taskId,
 * sessionStorage persist, the same shape-validating merge,
 * race-guarded `hydrateFromList`, optimistic `markCanceling`, and
 * the same FAST/SLOW cadence trigger set (`selectHasActiveTasks`).
 *
 * The "standalone vs mission-leaf" question is NOT answered here —
 * this store knows nothing about missions. Plan §E.1's merge logic
 * (S3) joins missions + tasks at render time via
 * `MissionNodeState.taskId` set intersection.
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md §G.2 + §J.S2.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";
import type {
  HarnTaskRecord,
  HarnTaskStatus,
  HarnTaskSummary,
} from "@/api/lx-harn";

export interface HarnTaskSlot {
  summary: HarnTaskSummary;
  record?: HarnTaskRecord;
  lastFetchedAt: number;
  isCanceling?: boolean;
}

interface HarnTasksState {
  tasks: Map<string, HarnTaskSlot>;
  activeCaseId: string | null;
  pollError: string | null;
  lastPolledAt: number | null;
}

interface HarnTasksActions {
  hydrateFromList: (
    summaries: HarnTaskSummary[],
    opts?: { caseId?: string },
  ) => void;
  upsertTask: (record: HarnTaskRecord) => void;
  markCanceling: (taskId: string) => void;
  removeTask: (taskId: string) => void;
  clear: () => void;
  setActiveCaseId: (caseId: string | null) => void;
  setPollError: (msg: string | null) => void;
  setLastPolledAt: (ts: number) => void;
}

export type HarnTasksStoreState = HarnTasksState & HarnTasksActions;

const INITIAL: HarnTasksState = {
  tasks: new Map(),
  activeCaseId: null,
  pollError: null,
  lastPolledAt: null,
};

function slotFromSummary(
  s: HarnTaskSummary,
  prev: HarnTaskSlot | undefined,
): HarnTaskSlot {
  return {
    summary: s,
    record: prev?.record,
    lastFetchedAt: Date.now(),
    isCanceling: prev?.isCanceling,
  };
}

function slotFromRecord(
  r: HarnTaskRecord,
  prev: HarnTaskSlot | undefined,
): HarnTaskSlot {
  const summary: HarnTaskSummary = prev?.summary ?? {
    taskId: r.taskId,
    harnessId: r.harnessId,
    scope: r.scope,
    orgId: r.orgId,
    clientId: r.clientId,
    matterId: r.matterId,
    caseId: r.caseId,
    status: r.status,
    createdAt: r.createdAt,
    progress: r.progress,
    parentTaskId: r.parentTaskId,
    chainDepth: r.chainDepth,
    // ⚠ The record-first path used to drop this, so a task fetched in FULL
    // (HarnessDetailDialog) still synthesized `classification: undefined` and
    // the Tarefas infrastructure filter could not fire on it. Found by
    // `git-implementation` 2026-08-10 while I was busy claiming the filter was
    // already live — it was not, on either path.
    classification: r.classification,
  };
  return {
    summary: {
      ...summary,
      status: r.status,
      progress: r.progress,
    },
    record: r,
    lastFetchedAt: Date.now(),
    isCanceling: prev?.isCanceling,
  };
}

/** Exported for direct unit testing of the Map ↔ entries round-trip. */
export function partializeForPersist(state: HarnTasksStoreState): {
  tasks: Array<[string, HarnTaskSlot]>;
  activeCaseId: string | null;
} {
  return {
    tasks: Array.from(state.tasks.entries()),
    activeCaseId: state.activeCaseId,
  };
}

export function mergeFromPersist<S extends HarnTasksStoreState>(
  persisted: unknown,
  current: S,
): S {
  const p = persisted as
    | { tasks?: unknown; activeCaseId?: string | null }
    | undefined;
  const rawEntries = p?.tasks;
  const rawArray: Array<[string, HarnTaskSlot]> = Array.isArray(rawEntries)
    ? (rawEntries as Array<[string, HarnTaskSlot]>)
    : [];
  const entries = rawArray.filter((e): e is [string, HarnTaskSlot] => {
    if (!Array.isArray(e) || e.length !== 2) return false;
    const [id, slot] = e;
    if (typeof id !== "string" || !id) return false;
    if (!slot || typeof slot !== "object") return false;
    const sl = slot as Partial<HarnTaskSlot>;
    if (!sl.summary || typeof sl.summary !== "object") return false;
    if (typeof sl.summary.taskId !== "string") return false;
    if (typeof sl.summary.status !== "string") return false;
    return true;
  });
  return {
    ...current,
    ...(p ?? {}),
    tasks: new Map(entries),
  } as S;
}

export const useHarnTasksStore = create<HarnTasksStoreState>()(
  persist(
    (set) => ({
      ...INITIAL,

      hydrateFromList: (summaries, opts) =>
        set((s) => {
          if (opts?.caseId && opts.caseId !== s.activeCaseId) {
            return {};
          }
          const next = new Map(s.tasks);
          if (opts?.caseId) {
            const incoming = new Set(summaries.map((t) => t.taskId));
            for (const [id, slot] of next) {
              if (slot.summary.caseId === opts.caseId && !incoming.has(id)) {
                next.delete(id);
              }
            }
          } else {
            // CROSS-CASE RECONCILIATION. Previously the cross-case poll
            // (`caseId: null` — what the Agents sidebar uses) pruned
            // NOTHING, and slots persist to sessionStorage, so the pane
            // accumulated task rows indefinitely across sessions: 162
            // slots held against 100 the server actually returned, the
            // excess being rows from older builds that predate fields the
            // UI now reads. They rendered as current activity.
            //
            // The list is a PAGE (server default 100, max 500), so
            // absence alone cannot mean "gone" — a task older than the
            // page simply fell outside the window. Prune only slots that
            // are absent AND fall inside the returned window, i.e. no
            // older than the oldest row the server just sent. Anything
            // beyond the window is left untouched.
            const incoming = new Set(summaries.map((t) => t.taskId));
            let oldestReturnedAt = Infinity;
            for (const t of summaries) {
              if (typeof t.createdAt === "number" && t.createdAt < oldestReturnedAt) {
                oldestReturnedAt = t.createdAt;
              }
            }
            if (summaries.length > 0 && Number.isFinite(oldestReturnedAt)) {
              for (const [id, slot] of next) {
                const createdAt = slot.summary.createdAt;
                if (
                  !incoming.has(id) &&
                  typeof createdAt === "number" &&
                  createdAt >= oldestReturnedAt
                ) {
                  next.delete(id);
                }
              }
            }
          }
          for (const t of summaries) {
            next.set(t.taskId, slotFromSummary(t, next.get(t.taskId)));
          }
          return {
            tasks: next,
            lastPolledAt: Date.now(),
            pollError: null,
          };
        }),

      upsertTask: (record) =>
        set((s) => {
          const next = new Map(s.tasks);
          next.set(record.taskId, slotFromRecord(record, next.get(record.taskId)));
          return { tasks: next };
        }),

      markCanceling: (taskId) =>
        set((s) => {
          const slot = s.tasks.get(taskId);
          if (!slot) return {};
          const next = new Map(s.tasks);
          next.set(taskId, { ...slot, isCanceling: true });
          return { tasks: next };
        }),

      removeTask: (taskId) =>
        set((s) => {
          if (!s.tasks.has(taskId)) return {};
          const next = new Map(s.tasks);
          next.delete(taskId);
          return { tasks: next };
        }),

      clear: () => set({ ...INITIAL, tasks: new Map() }),
      setActiveCaseId: (caseId) => set({ activeCaseId: caseId }),
      setPollError: (msg) => set({ pollError: msg }),
      setLastPolledAt: (ts) => set({ lastPolledAt: ts }),
    }),
    {
      name: "lexide:harn-tasks",
      storage: createJSONStorage(() => createTenantScopedStorage(() => sessionStorage)),
      partialize: partializeForPersist,
      merge: mergeFromPersist,
    },
  ),
);

// ─── Selectors ────────────────────────────────────────────────────────

// "ACTIVE" set for FAST cadence — mirrors useMissionsStore: excludes
// `awaiting_user` (parked tasks don't benefit from 5s polling).
const ACTIVE_STATUSES: ReadonlySet<HarnTaskStatus> = new Set<HarnTaskStatus>([
  "queued",
  "dispatched",
  "running",
]);

const SHOULD_AUTO_EXPAND_STATUSES: ReadonlySet<HarnTaskStatus> = new Set<HarnTaskStatus>([
  ...ACTIVE_STATUSES,
  "awaiting_user",
]);

export const selectTasksByCase = (
  s: HarnTasksState,
  caseId?: string,
): HarnTaskSlot[] => {
  const out: HarnTaskSlot[] = [];
  for (const slot of s.tasks.values()) {
    if (!caseId || slot.summary.caseId === caseId) out.push(slot);
  }
  return out;
};

export const selectHasActiveTasks = (s: HarnTasksState): boolean => {
  for (const slot of s.tasks.values()) {
    if (ACTIVE_STATUSES.has(slot.summary.status)) return true;
  }
  return false;
};

/**
 * Right-sidebar §I.2 auto-expand discriminator for harness tasks —
 * mirrors `selectShouldAutoExpand` in useMissionsStore.
 *
 * ⚠ **Infrastructure runs must never pop the sidebar open.** Checkpoints and
 * reconcilers are filtered OUT of the Tarefas list (`partitionInfrastructure`),
 * so a checkpoint that auto-expanded would swing the pane open onto a panel
 * with nothing in it — the row that triggered it is hidden by design. That
 * reads as a broken sidebar, and it is the same defect the filter exists to
 * fix (plumbing commanding the lawyer's attention) wearing a worse costume.
 *
 * Fail-visible, as everywhere else in this contract: only an explicit
 * `"infrastructure"` is skipped, so an absent or unrecognised classification
 * still expands exactly as it does today.
 */
export const selectHarnShouldAutoExpand = (s: HarnTasksState): boolean => {
  for (const slot of s.tasks.values()) {
    if (slot.summary.classification === "infrastructure") continue;
    if (SHOULD_AUTO_EXPAND_STATUSES.has(slot.summary.status)) return true;
  }
  return false;
};

declare global {
  interface Window {
    __harnTasksStore?: typeof useHarnTasksStore;
  }
}

if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__harnTasksStore", {
    value: useHarnTasksStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
